import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Search, ExternalLink } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, formatApiError } from "@/lib/api";

const STATUS = {
  unverified: { label: "Prüfung ausstehend", cls: "bg-muted text-charcoal/70 border-sand" },
  verified: { label: "Verifiziert", cls: "bg-gold/10 text-gold border-gold/30" },
  rejected: { label: "Verifizierung abgelehnt", cls: "bg-destructive/10 text-destructive border-destructive/30" },
  deactivated: { label: "Deaktiviert", cls: "bg-charcoal/10 text-charcoal/70 border-charcoal/20" },
};

const REJECT_REASONS = [
  "Unternehmen konnte nicht verifiziert werden",
  "Keine gewerbliche Tätigkeit",
  "Registrierung durch Privatperson",
  "Unvollständige Angaben",
  "Unternehmensdaten nicht überprüfbar",
  "Sonstiger Grund",
];

const EDIT_FIELDS = [
  { key: "firmenname", label: "Firmenname" },
  { key: "ansprechpartner", label: "Ansprechpartner" },
  { key: "telefon", label: "Telefon" },
  { key: "strasse", label: "Straße" },
  { key: "hausnummer", label: "Hausnummer" },
  { key: "plz", label: "PLZ" },
  { key: "ort", label: "Ort" },
  { key: "land", label: "Land" },
  { key: "homepage", label: "Homepage" },
  { key: "unternehmens_id", label: "Unternehmens-ID" },
];

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.unverified;
  return (
    <Badge variant="outline" className={`rounded-sm font-normal ${s.cls}`} data-testid={`status-badge-${status}`}>
      {s.label}
    </Badge>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function AdminPanel() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectComment, setRejectComment] = useState("");

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/customers");
      setCustomers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    let list = [...customers];
    if (statusFilter !== "all") list = list.filter((c) => c.status === statusFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (c) =>
          (c.firmenname || "").toLowerCase().includes(q) ||
          (c.ansprechpartner || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      return sortOrder === "newest" ? db - da : da - db;
    });
    return list;
  }, [customers, statusFilter, search, sortOrder]);

  const action = async (id, verb, successMsg) => {
    setBusy(id + verb);
    try {
      await api.post(`/admin/customers/${id}/${verb}`);
      toast.success(successMsg);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const openReject = (c) => {
    setRejectTarget(c);
    setRejectReason("");
    setRejectComment("");
  };

  const confirmReject = async () => {
    if (!rejectReason) {
      toast.error("Bitte wählen Sie einen Grund.");
      return;
    }
    const id = rejectTarget.id;
    setBusy(id + "reject");
    try {
      await api.post(`/admin/customers/${id}/reject`, {
        reason: rejectReason,
        internal_comment: rejectComment,
      });
      toast.success("Verifizierung abgelehnt. Der Kunde wurde benachrichtigt.");
      setRejectTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const openEdit = (c) => {
    setEditTarget(c);
    setEditForm({
      firmenname: c.firmenname || "",
      ansprechpartner: c.ansprechpartner || "",
      telefon: c.telefon || "",
      strasse: c.strasse || "",
      hausnummer: c.hausnummer || "",
      plz: c.plz || "",
      ort: c.ort || "",
      land: c.land || "",
      homepage: c.homepage || "",
      unternehmens_id: c.unternehmens_id || "",
    });
  };

  const saveEdit = async () => {
    const id = editTarget.id;
    setBusy(id + "edit");
    try {
      await api.put(`/admin/customers/${id}`, editForm);
      toast.success("Kundendaten aktualisiert.");
      setEditTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const confirmDelete = async () => {
    const id = toDelete.id;
    setToDelete(null);
    setBusy(id + "delete");
    try {
      await api.delete(`/admin/customers/${id}`);
      toast.success("Kunde wurde gelöscht.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="admin-page">
      <AppHeader to="/admin" />
      <main className="flex-1 w-full max-w-6xl mx-auto px-5 sm:px-6 py-10 sm:py-12">
        <h1 className="font-serif text-3xl sm:text-4xl font-light text-charcoal">Kundenverwaltung</h1>
        <p className="mt-3 text-sm text-charcoal/60">
          {loading ? "Wird geladen…" : `${customers.length} registrierte Kunden`}
        </p>

        {!loading && customers.length > 0 && (
          <div className="mt-8 flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="flex-1 space-y-1.5">
              <label className="text-xs text-charcoal/50" htmlFor="admin-search">Suche</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal/40" strokeWidth={1.5} />
                <Input
                  id="admin-search"
                  placeholder="Firmenname, Ansprechpartner oder E-Mail…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="admin-search-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-charcoal/50">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full lg:w-56" data-testid="admin-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="unverified">Prüfung ausstehend</SelectItem>
                  <SelectItem value="verified">Verifiziert</SelectItem>
                  <SelectItem value="rejected">Verifizierung abgelehnt</SelectItem>
                  <SelectItem value="deactivated">Deaktiviert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-charcoal/50">Sortierung</label>
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger className="w-full lg:w-44" data-testid="admin-sort-order">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Neueste</SelectItem>
                  <SelectItem value="oldest">Älteste</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {!loading && customers.length === 0 && (
          <p className="mt-16 text-center text-charcoal/50" data-testid="admin-empty">
            Es sind noch keine Kunden registriert.
          </p>
        )}

        {!loading && customers.length > 0 && visible.length === 0 && (
          <p className="mt-16 text-center text-charcoal/50" data-testid="admin-no-results">
            Keine Kunden entsprechen Ihrer Suche.
          </p>
        )}

        <div className="mt-8 space-y-4">
          {visible.map((c) => (
            <div key={c.id} className="bg-white border border-sand rounded-sm p-5 sm:p-6" data-testid={`customer-row-${c.email}`}>
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-serif text-xl text-charcoal">{c.firmenname}</h3>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="mt-3 grid gap-1 text-sm text-charcoal/70 sm:grid-cols-2">
                    <p><span className="text-charcoal/45">Ansprechpartner:</span> {c.ansprechpartner}</p>
                    <p><span className="text-charcoal/45">E-Mail:</span> {c.email}</p>
                    <p><span className="text-charcoal/45">Telefon:</span> {c.telefon}</p>
                    <p><span className="text-charcoal/45">Adresse:</span> {c.adresse}</p>
                    <p className="flex items-center gap-1">
                      <span className="text-charcoal/45">Homepage:</span>{" "}
                      {c.homepage ? (
                        <a
                          href={/^https?:\/\//i.test(c.homepage) ? c.homepage : `https://${c.homepage}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gold hover:text-golddark inline-flex items-center gap-1"
                          data-testid={`homepage-link-${c.email}`}
                        >
                          {c.homepage} <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                        </a>
                      ) : "—"}
                    </p>
                    <p><span className="text-charcoal/45">Unternehmens-ID:</span> {c.unternehmens_id || "—"}</p>
                    <p><span className="text-charcoal/45">Registriert:</span> {formatDate(c.created_at)}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 shrink-0">
                  {c.status !== "verified" && (
                    <Button size="sm" onClick={() => action(c.id, "verify", "Kunde verifiziert. Bestätigungs-E-Mail versendet.")} disabled={!!busy}
                      className="bg-gold hover:bg-golddark text-white rounded-sm transition-colors duration-500" data-testid={`verify-button-${c.email}`}>
                      Verifizieren
                    </Button>
                  )}
                  {c.status === "unverified" && (
                    <Button size="sm" variant="outline" onClick={() => openReject(c)} disabled={!!busy}
                      className="border-destructive/40 text-destructive hover:bg-destructive/10 rounded-sm" data-testid={`reject-button-${c.email}`}>
                      Verifizierung ablehnen
                    </Button>
                  )}
                  {c.status !== "deactivated" && (
                    <Button size="sm" variant="outline" onClick={() => action(c.id, "deactivate", "Kunde deaktiviert.")} disabled={!!busy}
                      className="border-sand text-charcoal/70 hover:bg-muted rounded-sm" data-testid={`deactivate-button-${c.email}`}>
                      Deaktivieren
                    </Button>
                  )}
                  {c.status === "deactivated" && (
                    <Button size="sm" variant="outline" onClick={() => action(c.id, "activate", "Kunde aktiviert.")} disabled={!!busy}
                      className="border-gold text-gold hover:bg-gold hover:text-white rounded-sm transition-colors duration-500" data-testid={`activate-button-${c.email}`}>
                      Aktivieren
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(c)} disabled={!!busy}
                    className="border-sand text-charcoal/70 hover:bg-muted rounded-sm" data-testid={`edit-button-${c.email}`}>
                    Bearbeiten
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setToDelete(c)} disabled={!!busy}
                    className="text-destructive hover:bg-destructive/10 rounded-sm" data-testid={`delete-button-${c.email}`}>
                    Löschen
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">Verifizierung ablehnen</DialogTitle>
            <DialogDescription>
              Bitte wählen Sie einen Grund. Diese Angaben sind ausschließlich für den internen Gebrauch und werden dem Kunden nicht mitgeteilt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Grund</Label>
              <Select value={rejectReason} onValueChange={setRejectReason}>
                <SelectTrigger data-testid="reject-reason-select">
                  <SelectValue placeholder="Grund wählen" />
                </SelectTrigger>
                <SelectContent>
                  {REJECT_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {rejectReason === "Sonstiger Grund" && (
              <div className="space-y-2">
                <Label>Interner Kommentar (nur für Administrator)</Label>
                <Textarea
                  rows={3}
                  value={rejectComment}
                  onChange={(e) => setRejectComment(e.target.value)}
                  data-testid="reject-comment-input"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)} className="rounded-sm" data-testid="reject-cancel-button">
              Abbrechen
            </Button>
            <Button onClick={confirmReject} disabled={!!busy} className="bg-destructive hover:bg-destructive/90 text-white rounded-sm" data-testid="reject-confirm-button">
              Ablehnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">Kundendaten bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {EDIT_FIELDS.map((f) => (
              <div key={f.key} className="space-y-2">
                <Label htmlFor={`edit-${f.key}`}>{f.label}</Label>
                <Input
                  id={`edit-${f.key}`}
                  value={editForm[f.key] ?? ""}
                  onChange={(e) => setEditForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  data-testid={`edit-${f.key}-input`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} className="rounded-sm" data-testid="edit-cancel-button">
              Abbrechen
            </Button>
            <Button onClick={saveEdit} disabled={!!busy} className="bg-gold hover:bg-golddark text-white rounded-sm transition-colors duration-500" data-testid="edit-save-button">
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl font-light">Kunde löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie diesen Kunden wirklich löschen?
              {toDelete && <span className="block mt-2 text-charcoal font-medium">{toDelete.firmenname}</span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="delete-cancel-button">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90 text-white rounded-sm" data-testid="delete-confirm-button">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
